import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, LogOut, User, Play, Settings, Crown } from 'lucide-react';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { trackNavigationClick, logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { NotificationBell, NotificationTray } from '@/shared/components/NotificationTray';
import {
  PRIMARY_NAV_ITEMS,
  SETTINGS_NAV_ITEM,
  MOBILE_NAV_ITEMS,
  UPGRADE_NAV_ITEM,
  shouldShowUpgradeNav,
  isActiveNavPath,
  filterNavForTrialWeek,
  type NavItem,
} from '@/shared/layout/appShell';
import { isTrialWeekOptionCEnabled } from '@/config/featureFlags';
import { useTrialPlan } from '@/features/trialWeek/useTrialPlan';
import { shouldHidePracticeTab } from '@/features/trialWeek/trialDashboardGate';
import { hasPaidAccess } from '@/utils/firebase/subscription';
import { trialDayFromEndDate } from '@/features/trialWeek/trialDay';
import { handoffVariant } from '@/utils/handoffVariant';

/** Handoff B and C: "Pass Plan · Day N of 7", one tap to the plan, while the trial week runs. */
export const PassPlanPill: React.FC = () => {
  const { userData } = useEnhancedAuth();
  // Same trial test StartQuizRedirect uses: a paid user with a future trialEndDate never sees trial copy.
  const day = userData?.accessStatus?.reason === 'trial' ? trialDayFromEndDate(userData.trialEndDate) : null;
  if (handoffVariant() === 'A' || day === null) return null;
  return (
    <Link
      to="/dashboard"
      data-testid="pass-plan-pill"
      className="flex items-center px-3 py-1.5 rounded-full text-xs font-extrabold whitespace-nowrap bg-primary-50 text-primary-700 border-2 border-primary-600"
      style={{ boxShadow: '0 3px 0 rgba(27,58,56,0.18)' }}
    >
      Pass Plan · Day {day} of 7
    </Link>
  );
};

/**
 * Sidebar — the .pen left-sidebar nav "spine" (desktop) + the bottom tab bar
 * (mobile). Replaces the old top Header on the logged-in app surface.
 *
 * Preserves everything the Header did on the app surface: route-aware active
 * highlight, notifications tray, user menu + sign-out, the quiz-in-progress
 * "Continue" signal, and the trackNavigationClick analytics on every nav item.
 */

// ---------------------------------------------------------------------------
// A single sidebar nav row (icon + label), highlighted as a selected pill when
// active — the .pen selected-item treatment (light-teal fill + teal text).
// ---------------------------------------------------------------------------
const SidebarNavRow: React.FC<{
  item: NavItem;
  active: boolean;
  onNavigate: (item: NavItem) => void;
}> = ({ item, active, onNavigate }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.path}
      aria-current={active ? 'page' : undefined}
      onClick={() => onNavigate(item)}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[15px] transition-colors ${
        active
          ? 'bg-primary-50 text-primary-600 font-extrabold'
          : 'text-neutral-600 font-bold hover:bg-neutral-50 hover:text-neutral-900'
      }`}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span className="truncate">{item.label}</span>
    </Link>
  );
};

// ---------------------------------------------------------------------------
// The upgrade door. Fires `subscription_clicked` with a per-entry-point
// `source` so GA4 can finally tell WHICH door people use — before this, the
// logged-in surface had no door at all and the event never fired (0 in 90d).
// ---------------------------------------------------------------------------
export function trackUpgradeClick(source: string, subscriptionStatus: string | null) {
  logAnalyticsEvent(ANALYTICS_EVENTS.SUBSCRIPTION_CLICKED, {
    subscriptionStatus,
    isLoggedIn: true,
    source,
    timestamp: Date.now(),
  });
}

// Gold "ledge" upgrade row for the desktop sidebar — the .pen celebrate accent,
// deliberately the only gold in the nav so it reads as the one paid action.
const SidebarUpgradeRow: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Link
    to={UPGRADE_NAV_ITEM.path}
    onClick={onClick}
    data-testid="sidebar-upgrade"
    className="flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[15px] font-extrabold bg-[rgb(var(--color-celebrate))] text-[rgb(var(--color-on-celebrate))] hover:bg-[rgb(var(--color-celebrate-deep))] transition-colors"
    style={{ boxShadow: '0 4px 0 rgb(var(--color-celebrate-deep))' }}
  >
    <Crown className="w-5 h-5 shrink-0" />
    <span className="truncate">{UPGRADE_NAV_ITEM.label}</span>
  </Link>
);

// ---------------------------------------------------------------------------
// Shared hook: quiz-in-progress signal (moved verbatim from the old Header so
// the "Continue Quiz" affordance survives the top-nav → sidebar move).
// ---------------------------------------------------------------------------
function useQuizInProgress() {
  const [quizInProgress, setQuizInProgress] = useState(false);
  const [currentQuizId, setCurrentQuizId] = useState<string | null>(null);

  const checkQuizStatus = useCallback(async () => {
    try {
      // Placeholder — the active-quiz lookup is wired elsewhere; the listeners
      // below keep this in sync when a quiz starts/completes.
    } catch (error) {
      console.error('Error checking quiz status:', error);
    }
  }, []);

  useEffect(() => {
    checkQuizStatus();
    const handleQuizProgressUpdate = () => checkQuizStatus();
    const handleQuizCompleted = () => {
      setQuizInProgress(false);
      setCurrentQuizId(null);
    };
    window.addEventListener('quizProgressUpdated', handleQuizProgressUpdate);
    window.addEventListener('storage', checkQuizStatus);
    window.addEventListener('quizDataCleared', checkQuizStatus);
    window.addEventListener('quizCompleted', handleQuizCompleted);
    return () => {
      window.removeEventListener('quizProgressUpdated', handleQuizProgressUpdate);
      window.removeEventListener('storage', checkQuizStatus);
      window.removeEventListener('quizDataCleared', checkQuizStatus);
      window.removeEventListener('quizCompleted', handleQuizCompleted);
    };
  }, [checkQuizStatus]);

  return { quizInProgress, currentQuizId };
}

// ---------------------------------------------------------------------------
// Desktop sidebar (hidden < lg)
// ---------------------------------------------------------------------------
const Sidebar: React.FC = () => {
  const { currentUser, userData, logOut } = useEnhancedAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { quizInProgress, currentQuizId } = useQuizInProgress();
  const trialPlanQuery = useTrialPlan();
  const hidePractice = shouldHidePracticeTab({
    flagOn: isTrialWeekOptionCEnabled(),
    accessReason: userData?.accessStatus?.reason,
    plan: trialPlanQuery.data,
    paid: hasPaidAccess(userData),
  });
  const primaryNav = filterNavForTrialWeek(PRIMARY_NAV_ITEMS, hidePractice);

  // Close the user menu on outside click.
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const handleNavigate = (item: NavItem) => {
    trackNavigationClick('sidebar_link', item.analyticsLabel, item.path, true, location.pathname);
  };

  const handleSignOut = async () => {
    try {
      trackNavigationClick('sidebar_link', 'Sign Out', '/', true, location.pathname);
      setUserMenuOpen(false);
      await logOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleContinueQuiz = () => {
    if (currentQuizId) {
      trackNavigationClick('sidebar_link', 'Continue Quiz', `/quiz/${currentQuizId}`, true, location.pathname);
      navigate(`/quiz/${currentQuizId}`);
    }
  };

  return (
    <aside className="hidden lg:flex flex-col w-[248px] shrink-0 bg-white border-r border-neutral-200 px-4 py-5 h-full overflow-y-auto">
      {/* Logo */}
      <Link
        to="/dashboard"
        onClick={() =>
          trackNavigationClick('logo', 'SWTPA', '/dashboard', !!currentUser, location.pathname)
        }
        className="flex items-center gap-2.5 px-1.5 py-1.5 group"
      >
        <span className="flex items-center justify-center w-9 h-9 rounded-[10px] bg-primary-600 text-white">
          <BookOpen className="w-5 h-5" />
        </span>
        <span className="font-display text-xl font-semibold text-neutral-900">SWTPA</span>
      </Link>

      <div className="pt-3 flex empty:hidden">
        <PassPlanPill />
      </div>

      {/* Primary nav */}
      <nav className="flex flex-col gap-1 pt-4">
        {primaryNav.map((item) => (
          <SidebarNavRow
            key={item.path}
            item={item}
            active={isActiveNavPath(item.path, location.pathname)}
            onNavigate={handleNavigate}
          />
        ))}
        {quizInProgress && (
          <button
            onClick={handleContinueQuiz}
            className="mt-1 flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[15px] font-extrabold bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            <Play className="w-5 h-5 shrink-0" />
            <span>Continue Quiz</span>
          </button>
        )}
      </nav>

      {/* Spacer pushes the user block + Settings to the bottom */}
      <div className="flex-1" />

      {/* User block: notifications + user menu + sign-out */}
      {currentUser && (
        <div className="flex items-center gap-1 mb-2">
          <div className="relative">
            <NotificationBell />
            <NotificationTray placement="top" />
          </div>
          <div className="relative flex-1 min-w-0" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              data-testid="user-menu"
              className="w-full flex items-center gap-2 px-2 py-2 rounded-2xl hover:bg-neutral-50 transition-colors"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-50 shrink-0">
                <User className="w-4 h-4 text-primary-600" />
              </span>
              <span className="body-small font-semibold text-neutral-700 truncate">
                {userData?.profile?.name || 'Menu'}
              </span>
            </button>
            {userMenuOpen && (
              <div className="absolute bottom-full mb-2 inset-x-0 bg-white rounded-2xl shadow-ledge-lg border border-neutral-200 py-2 animate-fade-in">
                <button
                  onClick={handleSignOut}
                  data-testid="logout-button"
                  className="w-full text-left px-4 py-2.5 body-small text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upgrade (only when the backend says this user has no access yet) */}
      {shouldShowUpgradeNav(userData?.hasFullAccess) && (
        <SidebarUpgradeRow
          onClick={() => {
            trackNavigationClick('sidebar_link', UPGRADE_NAV_ITEM.analyticsLabel, UPGRADE_NAV_ITEM.path, true, location.pathname);
            trackUpgradeClick('sidebar_upgrade', userData?.subscriptionStatus ?? null);
          }}
        />
      )}

      {/* Divider + Settings pinned to the bottom */}
      <div className="h-px bg-neutral-200 my-2" />
      <SidebarNavRow
        item={SETTINGS_NAV_ITEM}
        active={isActiveNavPath(SETTINGS_NAV_ITEM.path, location.pathname)}
        onNavigate={handleNavigate}
      />
    </aside>
  );
};

// ---------------------------------------------------------------------------
// Mobile bottom tab bar (hidden >= lg). Matches the .pen iPhone frames + iOS.
// ---------------------------------------------------------------------------
export const MobileTabBar: React.FC = () => {
  const location = useLocation();
  const { userData } = useEnhancedAuth();
  const trialPlanQuery = useTrialPlan();
  const hidePractice = shouldHidePracticeTab({
    flagOn: isTrialWeekOptionCEnabled(),
    accessReason: userData?.accessStatus?.reason,
    plan: trialPlanQuery.data,
    paid: hasPaidAccess(userData),
  });
  const mobileNav = filterNavForTrialWeek(MOBILE_NAV_ITEMS, hidePractice);

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 flex justify-around bg-white border-t border-neutral-200 px-1 pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      {mobileNav.map((item) => {
        const active = isActiveNavPath(item.path, location.pathname);
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            aria-current={active ? 'page' : undefined}
            onClick={() =>
              trackNavigationClick('bottom_tab', item.analyticsLabel, item.path, true, location.pathname)
            }
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[11px] font-bold transition-colors ${
              active ? 'text-primary-600' : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            <Icon className="w-5 h-5" />
            <span className="truncate max-w-full">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Mobile top bar (hidden >= lg). The desktop sidebar footer (account menu,
// notifications, Settings, Sign Out) has no home on mobile — the bottom tab bar
// only carries the primary destinations — so this puts them in a top bar's
// account menu. Without it there was no way to reach Settings or log out on a phone.
// ---------------------------------------------------------------------------
export const MobileTopBar: React.FC = () => {
  const { currentUser, userData, logOut } = useEnhancedAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleSignOut = async () => {
    try {
      trackNavigationClick('mobile_menu', 'Sign Out', '/', true, location.pathname);
      setMenuOpen(false);
      await logOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between bg-white border-b border-neutral-200 px-4 h-14">
      <Link
        to="/dashboard"
        onClick={() => trackNavigationClick('logo', 'SWTPA', '/dashboard', !!currentUser, location.pathname)}
        className="flex items-center gap-2"
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-[10px] bg-primary-600 text-white">
          <BookOpen className="w-4 h-4" />
        </span>
        <span className="font-display text-lg font-semibold text-neutral-900">SWTPA</span>
      </Link>

      {currentUser && (
        <div className="flex items-center gap-1">
          <PassPlanPill />
          {/* Upgrade pill — the bottom tab bar is full (5 primary destinations),
              so the phone's only route to the offer lives here, always visible
              rather than buried in the account menu. */}
          {shouldShowUpgradeNav(userData?.hasFullAccess) && (
            <Link
              to={UPGRADE_NAV_ITEM.path}
              data-testid="mobile-upgrade"
              onClick={() => {
                trackNavigationClick('mobile_menu', UPGRADE_NAV_ITEM.analyticsLabel, UPGRADE_NAV_ITEM.path, true, location.pathname);
                trackUpgradeClick('mobile_topbar_upgrade', userData?.subscriptionStatus ?? null);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold bg-[rgb(var(--color-celebrate))] text-[rgb(var(--color-on-celebrate))]"
              style={{ boxShadow: '0 3px 0 rgb(var(--color-celebrate-deep))' }}
            >
              <Crown className="w-3.5 h-3.5" />
              {UPGRADE_NAV_ITEM.label}
            </Link>
          )}
          <div className="relative">
            <NotificationBell />
            <NotificationTray placement="bottom" />
          </div>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-user-menu"
              aria-label="Account menu"
              aria-expanded={menuOpen}
              className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-50"
            >
              <User className="w-4 h-4 text-primary-600" />
            </button>
            {menuOpen && (
              <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl shadow-ledge-lg border border-neutral-200 py-2 animate-fade-in">
                <div className="px-4 py-2 border-b border-neutral-100">
                  <p className="body-small font-semibold text-neutral-900 truncate">
                    {userData?.profile?.name || 'Your account'}
                  </p>
                  {currentUser.email && (
                    <p className="text-xs text-neutral-500 truncate">{currentUser.email}</p>
                  )}
                </div>
                <Link
                  to={SETTINGS_NAV_ITEM.path}
                  onClick={() => setMenuOpen(false)}
                  className="w-full text-left px-4 py-2.5 body-small text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
                <button
                  onClick={handleSignOut}
                  data-testid="mobile-logout-button"
                  className="w-full text-left px-4 py-2.5 body-small text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Sidebar;
